// ============================================================
// PLATFORM SMART CARD LAYOUT ENGINE — SmartCard (motor agnóstico)
// ============================================================
// El motor NO sabe qué es un producto, vehículo, clínica, conductor
// o tenant. Solo organiza REGIONES visuales:
//   - Visual composition (shell, orden de regiones)
//   - Slot orchestration (qué regiones existen y en qué orden)
//   - Content distribution (espaciado, alineación, crecimiento)
//   - Adaptive spacing (gaps/padding por variante)
//   - Variant configuration (registry ESTRUCTURAL)
//   - Future extensibility (nuevas regiones = configuración)
//
// Todo el contenido llega YA COMPUESTO (ReactNode) desde el consumidor
// (p. ej. Kimberly Fashion monta SmartMedia + StatusBadge + datos en
// las regiones). Cero imports de negocio en este archivo.
//
// Política de media (heredada de index.css): object-fit: contain,
// NUNCA cover. El motor no toca el sistema de media.
// ============================================================
import React from 'react';
import { SmartCardProps, SmartCardVariant, SmartCardSpacing } from './SmartCard.types';
import { VARIANTS } from './SmartCard.variants';

const SPACING: Record<SmartCardSpacing, string> = {
  sm: 'gap-2 p-2',
  md: 'gap-0 p-0', // F1: el feed actual no usa gap interno (p-4 dentro de las regiones) — el gap lo gestiona el consumidor
  lg: 'gap-4 p-4',
};

export const SmartCard: React.FC<SmartCardProps> = ({
  variant = 'default',
  regions = {},
  spacing = 'md',
  className = '',
  as: Tag = 'div',
  onClick,
}) => {
  const cfg = VARIANTS[variant as SmartCardVariant] || VARIANTS.default;

  // Orden de regiones según la variante estructural (configuración pura)
  const ordered = cfg.regions
    .map((name) => ({ name, node: (regions as any)[name] }))
    .filter((r) => r.node != null);

  const shell = [cfg.shell, SPACING[spacing], className].filter(Boolean).join(' ');

  return (
    <Tag className={shell} onClick={onClick}>
      {ordered.map(({ name, node }) => {
        // La región overlay NO es una región del flujo: se monta DENTRO del
        // contenedor relativo de la región media (adorno absoluto sobre media).
        if (name === 'overlay') return null;
        // La región media + overlay comparten un contenedor relativo
        if (name === 'media') {
          return (
            <div key={name} className="relative">
              {node}
              {regions.overlay != null && <div className="absolute inset-0 pointer-events-none">{regions.overlay}</div>}
            </div>
          );
        }
        // La región footer se ancla al fondo cuando la variante lo pide
        if (name === 'footer' && cfg.footerAlign === 'bottom') {
          return (
            <div key={name} className="mt-auto">
              {node}
            </div>
          );
        }
        return <div key={name}>{node}</div>;
      })}
    </Tag>
  );
};

export default SmartCard;
